/**
 * Vanilla JS lightbox for the product-video-circles block.
 * Clicking a circle loads its video into a shared <dialog>; prev/next
 * navigate between circles without closing it. Closing clears the video
 * src so playback (and audio) actually stops.
 */
class VideoCircles extends HTMLElement {
  activeIndex = 0;

  /** @type {HTMLDialogElement} */
  get dialog() {
    const dialog = this.querySelector('[data-video-circles-dialog]');

    if (!(dialog instanceof HTMLDialogElement)) throw new Error('Dialog element not found');

    return dialog;
  }

  /** @type {HTMLVideoElement} */
  get frame() {
    const frame = this.querySelector('[data-video-circles-frame]');

    if (!(frame instanceof HTMLVideoElement)) throw new Error('Video frame element not found');

    return frame;
  }

  /** @type {HTMLElement | null} */
  get caption() {
    return this.querySelector('[data-video-circles-caption]');
  }

  /** @type {HTMLElement | null} */
  get prevButton() {
    return this.querySelector('[data-video-circles-prev]');
  }

  /** @type {HTMLElement | null} */
  get nextButton() {
    return this.querySelector('[data-video-circles-next]');
  }

  /** @type {HTMLElement[]} */
  get triggers() {
    return Array.from(this.querySelectorAll('[data-video-circles-trigger]'));
  }

  connectedCallback() {
    if (this.triggers.length <= 1) {
      this.prevButton?.setAttribute('hidden', '');
      this.nextButton?.setAttribute('hidden', '');
    }

    this.addEventListener('click', this.#handleClick);
    this.dialog.addEventListener('click', this.#handleDialogClick);
    this.dialog.addEventListener('close', this.#handleClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.dialog.removeEventListener('click', this.#handleDialogClick);
    this.dialog.removeEventListener('close', this.#handleClose);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const trigger = target.closest('[data-video-circles-trigger]');
    if (trigger instanceof HTMLElement) {
      const index = this.triggers.indexOf(trigger);
      if (index === -1) return;
      this.#showVideo(index);
      this.dialog.showModal();
      return;
    }

    if (target.closest('[data-video-circles-close]')) {
      this.dialog.close();
      return;
    }

    if (target.closest('[data-video-circles-next]')) {
      this.#showVideo((this.activeIndex + 1) % this.triggers.length);
      return;
    }

    if (target.closest('[data-video-circles-prev]')) {
      this.#showVideo((this.activeIndex - 1 + this.triggers.length) % this.triggers.length);
    }
  };

  /** @param {number} index */
  #showVideo(index) {
    const trigger = this.triggers[index];
    if (!trigger) return;

    this.activeIndex = index;
    this.frame.src = trigger.dataset.videoSrc || '';
    this.frame.load();
    this.frame.play().catch(() => {});

    if (this.caption) {
      this.caption.textContent = trigger.dataset.title || '';
    }
  }

  /** Clicking the native ::backdrop fires a click with the dialog itself as the target. */
  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    if (event.target === this.dialog) {
      this.dialog.close();
    }
  };

  #handleClose = () => {
    this.frame.src = '';
  };
}

if (!customElements.get('video-circles')) {
  customElements.define('video-circles', VideoCircles);
}
